import { useCallback, useEffect, useState } from 'react'
import { Formik, type FormikHelpers } from 'formik'
import { array, boolean, InferType, number, object, string } from 'yup'
import { useShallow } from 'zustand/react/shallow'

import CheckBox from '../../CheckBox'
import SearchInput from '../../SearchInput'
import useAuth from '../../../hooks/useAuth'
import useSaveBuildAuto from '../../../hooks/useSaveBuildAuto'
import useNewUpload, {
  getStateFromAnalysedFile,
  type TNewUploadAnalysedFile,
} from '../../../hooks/useNewUpload'
import BuildEgtDisclaimer from './BuildEgtDisclaimer'
import NexusAvailableModal from './NexusAvailableModal'

type Props = {
  analysedFile: TNewUploadAnalysedFile
  formId: string
  onRequestManual: () => void
  onSubmitSuccess: () => void
}

const buildAutoSchema = object({
  dtcOnly: boolean().required().default(false),
  removedDTCs: array(string().required()).required().default([]),
  selectedMods: array(number().required()).required().default([]),
}).required()

type TBuildAutoValues = InferType<typeof buildAutoSchema>

const BUILD_AUTO_SELECTION_ERROR =
  'Select at least one modification or DTC delete before continuing'
const BUILD_AUTO_DTC_ONLY_SELECTION_ERROR =
  'At least one DTC must be selected for a DTC only build'

const sortAlphaNumeric = (left: string, right: string) => {
  return left.localeCompare(right, undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

const getHasSelectedEgt = (
  availableMods: NonNullable<TNewUploadAnalysedFile['auto']>['availableMods'],
  selectedMods: number[],
) => {
  return availableMods.some((mod) => {
    return (
      mod.modCode?.toUpperCase() === 'EGT' && selectedMods.includes(mod.id || 0)
    )
  })
}

const BuildAuto = (props: Props) => {
  const { analysedFile, formId, onRequestManual, onSubmitSuccess } = props
  const [auth] = useAuth()
  const saveBuildAutoMutation = useSaveBuildAuto()
  const { build, isNexusAvailable, setState } = useNewUpload(
    useShallow((state) => ({
      build: state.build,
      isNexusAvailable: state.isNexusAvailable,
      setState: state.setState,
    })),
  )
  const [dtcSearch, setDtcSearch] = useState('')
  const [nexusAvailableModalOpen, setNexusAvailableModalOpen] = useState(false)
  const isMasterDealer = auth.membership.dealer.type === 'MASTER'

  const sortedAvailableMods = [
    ...(analysedFile.auto?.availableMods || []),
  ].sort((left, right) => {
    return sortAlphaNumeric(left.modName || '', right.modName || '')
  })
  const sortedAvailableDTCs = [
    ...(analysedFile.auto?.availableDTCs || []),
  ].sort((left, right) => {
    return sortAlphaNumeric(left.displayName, right.displayName)
  })
  const filteredAvailableDTCs = sortedAvailableDTCs.filter((dtc) => {
    if (!dtcSearch.trim()) {
      return true
    }

    return dtc.displayName
      .toLowerCase()
      .includes(dtcSearch.trim().toLowerCase())
  })

  const initialValues: TBuildAutoValues = {
    dtcOnly: false,
    removedDTCs: analysedFile.auto?.removedDTCs || [],
    selectedMods: analysedFile.auto?.selectedMods || [],
  }
  const [hasSelectedEgt, setHasSelectedEgt] = useState(() =>
    getHasSelectedEgt(sortedAvailableMods, initialValues.selectedMods),
  )

  useEffect(() => {
    setHasSelectedEgt(
      getHasSelectedEgt(
        sortedAvailableMods,
        analysedFile.auto?.selectedMods || [],
      ),
    )
  }, [analysedFile.id, analysedFile?.auto?.selectedMods, sortedAvailableMods])

  useEffect(() => {
    if (!isNexusAvailable) {
      setNexusAvailableModalOpen(false)
      return
    }

    setNexusAvailableModalOpen(true)
  }, [analysedFile.id, isNexusAvailable])

  useEffect(() => {
    const nextDisabled = hasSelectedEgt && !build.egtDisclaimerAgreed

    if (build.nextDisabled === nextDisabled) {
      return
    }

    setState({
      build: {
        ...build,
        nextDisabled,
      },
    })
  }, [build, hasSelectedEgt, setState])

  const handleSubmit = useCallback(
    async (
      values: TBuildAutoValues,
      formikHelpers: FormikHelpers<TBuildAutoValues>,
    ) => {
      // "DTC only" is a UI-only flag. The server infers a DTC-only build from
      // saved automatic selections, so the extra validation has to happen here.
      if (values.dtcOnly && values.removedDTCs.length < 1) {
        formikHelpers.setStatus(BUILD_AUTO_DTC_ONLY_SELECTION_ERROR)
        return
      }

      if (values.removedDTCs.length < 1 && values.selectedMods.length < 1) {
        formikHelpers.setStatus(BUILD_AUTO_SELECTION_ERROR)
        return
      }

      formikHelpers.setStatus(undefined)

      setState({
        build: {
          ...build,
          isSaving: true,
        },
      })

      try {
        const saveResponse = await saveBuildAutoMutation.mutateAsync({
          analysedFileId: analysedFile.id,
          removedDTCs: values.removedDTCs,
          selectedMods: values.selectedMods,
        })

        setState(getStateFromAnalysedFile(saveResponse.analysedFile))
        onSubmitSuccess()
      } catch (err) {
        setState({
          build: {
            ...build,
            isSaving: false,
          },
        })

        throw err
      }
    },
    [analysedFile.id, build, onSubmitSuccess, saveBuildAutoMutation, setState],
  )

  return (
    <>
      <Formik<TBuildAutoValues>
        initialValues={initialValues}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={buildAutoSchema}
        onSubmit={handleSubmit}
      >
        {(formik) => {
          const handleModChange = (modId: number, checked: boolean) => {
            formik.setStatus(undefined)
            formik.setFieldValue(
              'selectedMods',
              checked
                ? formik.values.selectedMods.filter((value) => value !== modId)
                : [...formik.values.selectedMods, modId],
            )
          }

          const handleDtcOnlyChange = () => {
            const isSelected = formik.values.dtcOnly

            formik.setStatus(undefined)
            // Selecting DTC-only clears real mod selections because the server
            // treats "removed DTCs + no mods" as the DTC-only automatic flow.
            formik.setValues({
              ...formik.values,
              dtcOnly: !isSelected,
              selectedMods: isSelected ? formik.values.selectedMods : [],
            })
          }

          const handleDtcChange = (displayName: string, checked: boolean) => {
            formik.setStatus(undefined)
            // prettier-ignore
            formik.setFieldValue(
              'removedDTCs',
              checked
                ? formik.values.removedDTCs.filter(
                  (value) => value !== displayName,
                )
                : [...formik.values.removedDTCs, displayName],
            )
          }

          const submitError =
            typeof formik.status === 'string' ? formik.status : null
          const dtcOnlySelected = formik.values.dtcOnly

          return (
            <form id={formId} onSubmit={formik.handleSubmit}>
              <div className="flex items-center justify-center border-b border-gray-200 px-6 py-2.5">
                <div className="text-sm" role="img" aria-label="Fire">
                  🔥
                </div>
                <p className="ml-2 text-sm leading-5 text-gray-700">
                  Topgear Nexus is available for this file.
                </p>
              </div>
              <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-5">
                <div className="col-span-5 border-r border-gray-200 bg-gray-50 p-6 sm:col-span-3 md:col-span-2">
                  <h3 className="block text-sm font-medium leading-5 text-black">
                    Modifications
                  </h3>
                  <p className="pb-4 text-sm text-gray-600">
                    Choose how you wish to modify this file.
                  </p>
                  <div className="space-y-2">
                    {sortedAvailableMods.map((mod) => {
                      const modId = mod.id || 0
                      const checked = formik.values.selectedMods.includes(modId)
                      const disabled = dtcOnlySelected

                      return (
                        <CheckBox
                          key={`${modId}-${mod.modCode || mod.modName}`}
                          checked={checked}
                          description={
                            mod.modDesc ? (
                              <span className="text-xs">{mod.modDesc}</span>
                            ) : null
                          }
                          disabled={disabled}
                          label={mod.modName}
                          onChange={() => {
                            // prettier-ignore
                            const nextSelectedMods = checked
                              ? formik.values.selectedMods.filter(
                                (value) => value !== modId,
                              )
                              : [...formik.values.selectedMods, modId]

                            setHasSelectedEgt(
                              getHasSelectedEgt(
                                sortedAvailableMods,
                                nextSelectedMods,
                              ),
                            )
                            handleModChange(modId, checked)
                          }}
                        />
                      )
                    })}
                    <CheckBox
                      checked={dtcOnlySelected}
                      label="DTC only"
                      onChange={() => {
                        if (!dtcOnlySelected) {
                          setHasSelectedEgt(false)
                        }

                        handleDtcOnlyChange()
                      }}
                    />
                    {dtcOnlySelected && isMasterDealer && (
                      <div className="mt-4 text-xs rounded-md border border-amber-200 bg-orange-100 p-3 text-amber-900">
                        This file will be billed at £15 plus VAT (if
                        applicable). Note that this file will NOT include any
                        tune or solutions. For example, if you require an adblue
                        delete this will not be successful on a DTC only upload.
                      </div>
                    )}
                  </div>
                  <button
                    className="mt-8 mb-6 text-sm text-gray-700 underline hover:cursor-pointer hover:text-black"
                    type="button"
                    onClick={onRequestManual}
                  >
                    Request a manual file instead
                  </button>
                </div>

                <div className="col-span-5 p-6 sm:col-span-3 md:col-span-3">
                  <h3 className="block text-sm font-medium leading-5 text-black">
                    DTC deletes
                  </h3>
                  <p className="pb-4 text-sm text-gray-600">
                    Select the DTCs you wish to remove.
                  </p>
                  <SearchInput
                    placeholder="Search DTCs..."
                    onChange={setDtcSearch}
                  />
                  <div className="bg-gray-50 rounded-md p-2 mt-4 grid max-h-120 flex-1 grid-cols-2 gap-2 overflow-y-auto md:grid-cols-3">
                    {sortedAvailableDTCs.length < 1 && (
                      <p className="text-sm text-gray-600 col-span-full">
                        No DTC options are available.
                      </p>
                    )}
                    {
                      // prettier-ignore
                      isNexusAvailable &&
                      sortedAvailableDTCs.length > 0 &&
                      filteredAvailableDTCs.length < 1 && (
                        <p className="col-span-full text-sm text-gray-600">
                          No matching DTCs were found.
                        </p>
                      )
                    }
                    {filteredAvailableDTCs.map((dtc) => {
                      const checked = formik.values.removedDTCs.includes(
                        dtc.displayName,
                      )

                      return (
                        <CheckBox
                          key={dtc.displayName}
                          checked={checked}
                          label={dtc.displayName}
                          onChange={() =>
                            handleDtcChange(dtc.displayName, checked)
                          }
                        />
                      )
                    })}
                  </div>

                  {saveBuildAutoMutation.error?.message && (
                    <p className="mt-6 text-sm text-red-600">
                      {saveBuildAutoMutation.error.message}
                    </p>
                  )}
                  {submitError && (
                    <p className="mt-6 text-sm text-red-600">{submitError}</p>
                  )}
                  {hasSelectedEgt && (
                    <div className="mt-6">
                      <BuildEgtDisclaimer
                        checked={build.egtDisclaimerAgreed}
                        onChange={() =>
                          setState({
                            build: {
                              ...build,
                              egtDisclaimerAgreed: !build.egtDisclaimerAgreed,
                            },
                          })
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            </form>
          )
        }}
      </Formik>
      <NexusAvailableModal
        isOpen={isNexusAvailable && nexusAvailableModalOpen}
        onContinue={() => setNexusAvailableModalOpen(false)}
      />
    </>
  )
}

export default BuildAuto
